import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Pencil, Plus, RotateCcw, X } from "lucide-react";
import MoisCalendrier, { VERT_RESERVE, moisDe } from "./MoisCalendrier";

/* « Calendrier » des cours privés : UN écran pour voir et régler (Jen, 31.10.2026 : « le
   calendrier et les disponibilités des profs, ça pourrait être un » ; « Apple style »). Sert à
   la console (Cours privés), à l'app école (Privés) et à l'app des profs (Privés) :
   · à gauche le mois — en vert plein, les jours qui ont un cours privé réservé (Jen,
     02.11.2026 : « clairement les jours réservés en une couleur comme le vert », « toutes ces
     couleurs n'apportent rien ») ; en gris clair, les jours où personne n'est disponible ;
   · à droite la journée choisie, de 9h à 19h, une colonne par prof : ses heures libres
     (teinte légère du prof), ses cours privés réservés (vert plein, comme le mois), ses cours
     de l'école (gris). Une seule salle : un cours réservé retire l'heure à tous les profs. Un
     prof n'est jamais montré libre pendant un de ses cours ;
   · « Modifier les disponibilités » (ou le crayon d'une colonne) : les plages du prof ce
     jour-là, puis « Ce jour seulement » ou « Tous les jeudis » (comme Outlook : une occurrence
     ou toute la série — la semaine type) ;
   · en dessous, la semaine type de chaque prof, un jour par pastille (touche : tous les …).
   `editables` : les profs que ce compte peut régler (console et app école : tous ; app d'un
   prof : lui-même). `onEnregistrer(prof_id, date, plages | null, toutes)` : null = revenir à
   la semaine type ce jour-là. Les apps des profs ne reçoivent pas les contacts des familles.
   `voirJour` ({ date, n }) : un autre bout de l'écran demande d'afficher ce jour (la liste des
   cours réservés de la console : toucher la date).
   `onAjouter({ prof_id, date, debut })` (console et app école, Jen 03.11.2026 : « on devrait
   pouvoir ajouter un cours en appuyant sur le calendrier ») : toucher une heure dans la colonne
   d'un prof ouvre « Ajouter un cours » à ce moment-là (au quart d'heure ; un jour passé : un
   cours déjà donné). Les disponibilités se règlent alors avec le crayon ou « Modifier les
   disponibilités ». Sans `onAjouter` (app des profs), toucher règle les disponibilités. */

const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const JOURS_SEMAINE = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];          // 0 = lundi
const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const enMin = (h) => { const [a, b] = String(h || "0:0").split(":").map(Number); return a * 60 + (b || 0); };
const enHeure = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const heure = (h) => String(h || "").replace(/^0/, "").replace(":00", "h").replace(":", "h");
const quand = (iso) => new Date(`${iso}T12:00:00`);
const jourLong = (iso) => { const d = quand(iso); return `${JOURS[d.getDay()].charAt(0).toUpperCase()}${JOURS[d.getDay()].slice(1)} ${d.getDate()} ${MOIS[d.getMonth()]}`; };
const isoDe = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const decaler = (iso, n) => { const d = quand(iso); d.setDate(d.getDate() + n); return isoDe(d); };
const jourSemaine = (iso) => (quand(iso).getDay() + 6) % 7;                     // 0 = lundi
const plagesTexte = (liste) => (liste || []).map((p) => `${heure(p.debut)}–${heure(p.fin)}`).join(", ");
const vide = { dispos: [], libres: [], cours: [], reservations: [] };
const TRAIT = "var(--border)";
const H = 44;                                                                    // pixels par heure
// Les trois sortes de blocs de la journée, et leur légende (les mêmes styles).
const STYLE_COURS = { background: "#F1F5F9", borderLeft: "3px solid #CBD5E1", color: "#64748B" };
const styleLibre = (couleur) => ({ background: `${couleur}1A`, borderLeft: `3px solid ${couleur}`, color: couleur });
const STYLE_RESERVE = { background: VERT_RESERVE, color: "#fff" };
/* Une disponibilité n'est JAMAIS dessinée sur un cours de l'école du même prof (Jen,
   02.11.2026 : « Disponible 15h30–18h » par-dessus la Compétition varsity de 17h). Le serveur
   les retire déjà (et le site ne propose pas ces heures) ; l'écran s'en assure aussi, pour
   qu'aucune donnée en retard ou réglée à la main ne puisse plus se superposer. */
const sansSesCours = (plages, cours) => (plages || []).flatMap((p) => {
  let morceaux = [[enMin(p.debut), enMin(p.fin)]];
  (cours || []).forEach((c) => {
    const a = enMin(c.debut);
    const b = enMin(c.fin);
    morceaux = morceaux.flatMap(([d, f]) => (b <= d || a >= f ? [[d, f]] : [...(d < a ? [[d, a]] : []), ...(b < f ? [[b, f]] : [])]));
  });
  return morceaux.filter(([d, f]) => f - d >= 15).map(([d, f]) => ({ ...p, debut: enHeure(d), fin: enHeure(f) }));
});
const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

/* Les plages d'un prof un jour : une ligne par plage (de … à …, par quart d'heure). */
const Plages = ({ lignes, onChange, ouverture, fermeture }) => {
  const heures = [];
  for (let m = enMin(ouverture); m <= enMin(fermeture); m += 15) heures.push(enHeure(m));
  const maj = (i, champs) => onChange(lignes.map((l, k) => (k === i ? { ...l, ...champs } : l)));
  return (
    <div className="rounded-2xl border" style={{ borderColor: TRAIT }}>
      {!lignes.length && <div className="px-4 py-3 text-sm text-[var(--muted)]" data-testid="dispos-pas-disponible">Pas disponible.</div>}
      {lignes.map((l, i) => (
        <div key={i} className="px-4 py-2.5 flex items-center gap-2 text-sm" style={i ? { borderTop: `1px solid ${TRAIT}` } : undefined} data-testid="dispos-plage">
          <select className="input-field !w-auto !py-1.5 tabular-nums" value={l.debut} onChange={(e) => maj(i, { debut: e.target.value })} aria-label="De">
            {heures.slice(0, -1).map((t) => <option key={t} value={t}>{heure(t)}</option>)}
          </select>
          <span className="text-[var(--muted)]">à</span>
          <select className="input-field !w-auto !py-1.5 tabular-nums" value={l.fin} onChange={(e) => maj(i, { fin: e.target.value })} aria-label="À">
            {heures.slice(1).map((t) => <option key={t} value={t}>{heure(t)}</option>)}
          </select>
          <button type="button" className="ml-auto p-1 text-[var(--muted)] hover:text-[#B91C1C]" onClick={() => onChange(lignes.filter((_, k) => k !== i))}
            aria-label="Retirer cette plage" data-testid="dispos-retirer"><X size={15} /></button>
        </div>
      ))}
    </div>
  );
};

/* Modifier les disponibilités d'un prof : ce jour seulement, ou tous les … (la semaine type). */
const Editeur = ({ cal, edition, editables, onFermer, onEnregistrer }) => {
  const [pid, setPid] = useState(edition.prof_id);
  const [toutes, setToutes] = useState(!!edition.toutes);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const jour = cal.jours.find((j) => j.date === edition.date) || { profs: {} };
  const p = cal.profs.find((x) => x.prof_id === pid) || {};
  const x = jour.profs[pid] || vide;
  const semaineDuJour = (p.semaine || []).filter((s) => s.jour === jourSemaine(edition.date));
  const depart = toutes && edition.toutes ? semaineDuJour : x.dispos;
  const [lignes, setLignes] = useState(() => (depart || []).map(({ debut, fin }) => ({ debut, fin })));
  useEffect(() => {
    const touche = (e) => { if (e.key === "Escape") onFermer(); };
    window.addEventListener("keydown", touche);
    return () => window.removeEventListener("keydown", touche);
  }, [onFermer]);
  const changerProf = (id) => {
    setPid(id);
    const y = jour.profs[id] || vide;
    const s = (cal.profs.find((q) => q.prof_id === id)?.semaine || []).filter((q) => q.jour === jourSemaine(edition.date));
    setLignes(((toutes && edition.toutes ? s : y.dispos) || []).map(({ debut, fin }) => ({ debut, fin })));
    setErreur("");
  };
  const ajouter = () => {
    const fin = lignes.length ? Math.max(...lignes.map((l) => enMin(l.fin))) : 9 * 60 - 60;
    const d = Math.min(enMin(cal.fermeture || "22:00") - 60, fin + 60);
    setLignes([...lignes, { debut: enHeure(d), fin: enHeure(Math.min(d + 120, enMin(cal.fermeture || "22:00"))) }]);
  };
  const nomJour = JOURS[quand(edition.date).getDay()];
  // Dans l'app d'un prof (il ne règle que les siennes), la fenêtre lui parle : « tes cours ».
  const moi = editables.length === 1;
  const chevauche = (x.cours || []).filter((c) => lignes.some((l) => enMin(l.debut) < enMin(c.fin) && enMin(c.debut) < enMin(l.fin)));
  const invalide = lignes.some((l) => enMin(l.fin) <= enMin(l.debut));
  const enregistrer = async (plages, tout) => {
    setEnvoi(true); setErreur("");
    try { await onEnregistrer(pid, edition.date, plages, tout); onFermer(); } catch (e) { setErreur(e.message || String(e)); }
    setEnvoi(false);
  };
  // Dans un portail, au-dessus de tout : dans l'app d'un prof, la fenêtre restait sous la barre
  // d'onglets sur iPhone et « Enregistrer » ne se touchait pas (Jen, 31.10.2026).
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-6" style={{ background: "rgba(15,27,45,0.45)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="bg-white w-full sm:max-w-[520px] rounded-t-3xl sm:rounded-3xl max-h-[92dvh] overflow-auto shadow-2xl" data-testid="dispos-editeur">
        <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: TRAIT }}>
          <div>
            <div className="font-display text-xl tracking-tight">Disponibilités</div>
            <div className="text-xs text-[var(--muted)] mt-0.5">{jourLong(edition.date)}</div>
          </div>
          <button type="button" className="ml-auto p-1 text-[var(--muted)] hover:text-[var(--text)]" onClick={onFermer} aria-label="Fermer" data-testid="dispos-fermer"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {editables.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              {cal.profs.filter((q) => editables.includes(q.prof_id)).map((q) => (
                <button key={q.prof_id} type="button" onClick={() => changerProf(q.prof_id)} data-testid={`dispos-prof-${q.prof_id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm"
                  style={pid === q.prof_id ? { background: q.couleur, borderColor: q.couleur, color: "#fff", fontWeight: 600 } : { borderColor: TRAIT, background: "#fff" }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: pid === q.prof_id ? "#fff" : q.couleur }} />{q.prenom}
                </button>
              ))}
            </div>
          )}
          <div>
            <div className="inline-flex w-full rounded-full p-0.5 text-sm" style={{ background: "#F1F5F9" }} data-testid="dispos-portee">
              {[[false, "Ce jour seulement"], [true, `Tous les ${nomJour}s`]].map(([v, l]) => (
                <button key={l} type="button" onClick={() => setToutes(v)} data-testid={`dispos-portee-${v ? "toutes" : "jour"}`}
                  className="flex-1 px-3 py-1.5 rounded-full" style={toutes === v ? { background: "#fff", fontWeight: 600, boxShadow: "0 1px 2px rgba(15,27,45,.12)" } : { color: "var(--muted)" }}>{l}</button>
              ))}
            </div>
            <p className="text-xs text-[var(--muted)] mt-2">
              {toutes ? `${moi ? "Ta semaine type" : `La semaine type de ${p.prenom}`} change : chaque ${nomJour}, sauf les jours réglés à part.`
                : `Seulement le ${jourLong(edition.date).toLowerCase()} ; les autres ${nomJour}s ne changent pas.`}
            </p>
          </div>
          {(x.cours.length > 0 || x.reservations.length > 0) && (
            <div className="text-xs text-[var(--muted)] space-y-0.5">
              {x.cours.length > 0 && <div>{moi ? "Tes" : "Ses"} cours ce jour-là : {x.cours.map((c) => `${c.nom} ${heure(c.debut)}–${heure(c.fin)}`).join(" · ")}</div>}
              {x.reservations.length > 0 && <div>Cours privés réservés (ne bougent pas) : {x.reservations.map((r) => `${heure(r.debut)}–${heure(r.fin)}`).join(" · ")}</div>}
            </div>
          )}
          <Plages lignes={lignes} onChange={setLignes} ouverture={cal.ouverture || "07:00"} fermeture={cal.fermeture || "22:00"} />
          <button type="button" className="btn-ghost text-sm" onClick={ajouter} data-testid="dispos-ajouter"><Plus size={14} /> Ajouter une plage</button>
          {chevauche.length > 0 && (
            <div className="text-xs flex items-start gap-1.5" style={{ color: "#B45309" }}>
              <AlertTriangle size={13} className="shrink-0 mt-px" />
              Chevauche {chevauche.map((c) => `${c.nom} (${heure(c.debut)}–${heure(c.fin)})`).join(", ")} : ces heures ne seront pas proposées aux familles.
            </div>
          )}
          {x.exception && !toutes && (
            <button type="button" className="text-sm inline-flex items-center gap-1.5" style={{ color: "var(--primary)" }} onClick={() => enregistrer(null, false)} data-testid="dispos-semaine-type">
              <RotateCcw size={13} /> Revenir à la semaine type{semaineDuJour.length ? ` (${plagesTexte(semaineDuJour)})` : " (pas disponible)"}
            </button>
          )}
          {erreur && <p className="text-sm rounded-xl px-3 py-2" style={{ color: "#B91C1C", background: "#FEF2F2" }}>{erreur}</p>}
        </div>
        <div className="px-6 pt-4 border-t flex items-center gap-2" style={{ borderColor: TRAIT, paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}>
          <button type="button" className="btn-ghost text-sm ml-auto" onClick={onFermer}>Annuler</button>
          <button type="button" className="btn-primary text-sm" disabled={invalide || envoi} onClick={() => enregistrer(lignes, toutes)} data-testid="dispos-enregistrer">
            <Check size={14} /> {envoi ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default function CalendrierPrives({ cal, mois, onMois, editables = [], onEnregistrer, aujourdhui = isoDe(new Date()), voirJour = null,
  onAjouter = null }) {
  const [seul, setSeul] = useState("");
  // Le quart d'heure survolé dans une colonne (« + 14h15 ») quand on peut y ajouter un cours.
  const [survol, setSurvol] = useState(null);         // { prof_id, m }
  const [choisi, setChoisi] = useState("");
  const [edition, setEdition] = useState(null);         // { prof_id, date, toutes }
  const profs = cal.profs.filter((p) => !seul || p.prof_id === seul);
  // Le mois (Jen, 02.11.2026) : UNE couleur, le vert plein des jours qui ont un cours réservé
  // (avec leur nombre dès 2) ; les disponibilités ne colorent plus tout le mois — un jour où
  // personne n'est disponible (école fermée, dimanche, congé) est juste en gris clair. Tous
  // les jours restent touchables (voir la journée, ouvrir un jour à part).
  const joursCal = useMemo(() => {
    const m = {};
    cal.jours.forEach((j) => {
      if (moisDe(j.date) !== mois) return;
      const rs = profs.flatMap((p) => ((j.profs[p.prof_id] || vide).reservations || []).map((r) => ({ ...r, qui: p.prenom })))
        .sort((a, b) => enMin(a.debut) - enMin(b.debut));
      const dispo = profs.some((p) => { const x = j.profs[p.prof_id] || vide; return (x.dispos || x.libres || []).length > 0; });
      m[j.date] = rs.length
        ? { ton: "reserve", nb: rs.length, passe: j.date < aujourdhui,
            titre: `${pluriel(rs.length, "cours réservé", "cours réservés")} : ${rs.map((r) => `${heure(r.debut)} ${r.qui}`).join(" · ")}` }
        : dispo ? { ton: "vide" } : { ton: "eteint", titre: j.ferme ? "École fermée" : "Personne de disponible" };
    });
    return m;
  }, [cal, mois, seul, aujourdhui]); // eslint-disable-line react-hooks/exhaustive-deps
  // Le jour affiché : aujourd'hui dans le mois en cours ; ailleurs, le premier jour réservé
  // (sinon le premier où quelqu'un est disponible).
  const jours = Object.keys(joursCal).sort();
  const jourChoisi = choisi && moisDe(choisi) === mois ? choisi
    : moisDe(aujourdhui) === mois ? aujourdhui
      : jours.find((d) => joursCal[d].ton === "reserve") || jours.find((d) => joursCal[d].ton === "vide") || `${mois}-01`;
  const j = cal.jours.find((x) => x.date === jourChoisi);
  const passe = jourChoisi < aujourdhui;
  const peut = editables.length > 0 && onEnregistrer && !passe;
  const ouvrir = (prof_id, date = jourChoisi, toutes = false) => setEdition({ prof_id: prof_id || editables.find((e) => !seul || e === seul) || editables[0], date, toutes });
  const allerAu = (d) => { setChoisi(d); if (moisDe(d) !== mois) onMois(moisDe(d)); };
  // Sur téléphone, la journée est sous le mois : toucher un jour l'amène à l'écran (sinon
  // rien ne semblait changer, et « Modifier mes disponibilités » restait caché).
  const refJournee = useRef(null);
  const choisirJour = (d) => {
    setChoisi(d);
    if (typeof window !== "undefined" && window.innerWidth < 1024) refJournee.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // Un jour demandé d'ailleurs (la liste des cours réservés) : il s'affiche et vient à l'écran.
  const refRacine = useRef(null);
  useEffect(() => {
    if (!voirJour?.date) return;
    allerAu(voirJour.date);
    setTimeout(() => refRacine.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  }, [voirJour?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  // La journée : de 9h à 19h, élargie à ce qui est prévu ce jour-là.
  let debut = 9 * 60;
  let fin = 19 * 60;
  Object.values(j?.profs || {}).forEach((x) => [...(x.libres || x.dispos || []), ...(x.cours || []), ...(x.reservations || [])].forEach((p) => {
    debut = Math.min(debut, Math.floor(enMin(p.debut) / 60) * 60);
    fin = Math.max(fin, Math.ceil(enMin(p.fin) / 60) * 60);
  }));
  const haut = ((fin - debut) / 60) * H;
  const y = (h) => ((enMin(h) - debut) / 60) * H;
  const heures = [];
  for (let m = debut; m <= fin; m += 60) heures.push(m);
  // Le quart d'heure sous le doigt (ou la souris), dans la colonne d'un prof.
  const quartDHeure = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const m = debut + Math.floor((((e.clientY - r.top) / H) * 60) / 15) * 15;
    return Math.max(debut, Math.min(fin - 15, m));
  };
  const enHeure = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  // Un cours déjà là (réservé, de l'école) ne s'ajoute pas par-dessus : le toucher ne fait rien.
  const surUnCours = (e) => !!e.target.closest?.('[data-testid="calendrier-prives-reserve"], [data-testid="calendrier-prives-cours"]');
  // Un bloc de la journée. Moins de 45 min (Jen, 02.11.2026 : « Eveil à la danse », l'horaire
  // disparaissait) : une seule ligne, l'horaire d'abord (toujours lisible), puis le titre ;
  // le tout en info-bulle.
  const bloc = (a, b, style, titre, horaire, testid, info, onClick = null) => {
    const court = enMin(b) - enMin(a) < 45;
    return (
      <div key={`${a}-${b}-${testid}`} title={info} data-testid={testid} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
        onClick={onClick || undefined} onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
        className={`absolute left-1 right-1 rounded-lg px-1.5 overflow-hidden text-[11px] leading-tight ${court ? "flex items-center" : "py-0.5"} ${onClick ? "cursor-pointer hover:brightness-95" : ""}`}
        style={{ top: y(a) + 1, height: Math.max(14, y(b) - y(a) - 2), ...style }}>
        {court
          ? <div className="truncate min-w-0"><span className="font-semibold tabular-nums">{horaire}</span>{titre ? <> {titre}</> : null}</div>
          : <><div className="font-semibold truncate">{titre}</div><div className="truncate tabular-nums">{horaire}</div></>}
      </div>
    );
  };
  // La prochaine date de ce jour de la semaine (pour « tous les … » depuis la semaine type).
  const prochain = (js) => { let d = aujourdhui; while (jourSemaine(d) !== js) d = decaler(d, 1); return d; };

  return (
    <div ref={refRacine} className="space-y-4 scroll-mt-6" data-testid="calendrier-prives">
      {cal.profs.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {[{ prof_id: "", prenom: "Tous", couleur: "#0F172A" }, ...cal.profs].map((p) => {
            const actif = seul === p.prof_id;
            return (
              <button key={p.prof_id || "tous"} type="button" onClick={() => setSeul(p.prof_id)} data-testid={`calendrier-prives-filtre-${p.prof_id || "tous"}`}
                className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium"
                style={actif ? { background: p.couleur, borderColor: p.couleur, color: "#fff" } : { borderColor: TRAIT, background: "#fff" }}>
                {p.prof_id && <span className="w-2 h-2 rounded-full" style={{ background: actif ? "#fff" : p.couleur }} />}{p.prenom}
              </button>
            );
          })}
        </div>
      )}
      <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-4 items-start">
        <div className="rounded-2xl border bg-white p-4 space-y-3" style={{ borderColor: TRAIT }}>
          <MoisCalendrier mois={mois} onMois={(m) => { setChoisi(""); onMois(m); }} jours={joursCal} choisi={jourChoisi}
            onChoisir={choisirJour} testid="calendrier-prives-mois" aujourdhui={aujourdhui} agenda />
          {/* La légende : exactement ce que montre le mois, rien de plus. */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-[var(--muted)] pt-3 border-t" style={{ borderColor: TRAIT }}
            data-testid="calendrier-prives-legende">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: VERT_RESERVE }} /> cours réservé</span>
            <span>en gris clair : personne de libre</span>
          </div>
        </div>

        <div ref={refJournee} className="rounded-2xl border bg-white overflow-hidden scroll-mt-24" style={{ borderColor: TRAIT }} data-testid="calendrier-prives-journee">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b" style={{ borderColor: TRAIT }}>
            <button type="button" className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#F1F5F9]" onClick={() => allerAu(decaler(jourChoisi, -1))}
              aria-label="Jour précédent" data-testid="calendrier-prives-jour-avant"><ChevronLeft size={16} /></button>
            <div className="font-semibold min-w-0 truncate" data-testid="calendrier-prives-titre">{jourLong(jourChoisi)}</div>
            <button type="button" className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#F1F5F9]" onClick={() => allerAu(decaler(jourChoisi, 1))}
              aria-label="Jour suivant" data-testid="calendrier-prives-jour-apres"><ChevronRight size={16} /></button>
            {jourChoisi !== aujourdhui && (
              <button type="button" className="rounded-full border px-2.5 py-1 text-xs font-medium hover:bg-[#F8FAFC]" style={{ borderColor: TRAIT }}
                onClick={() => allerAu(aujourdhui)} data-testid="calendrier-prives-aujourdhui">Aujourd'hui</button>
            )}
            {peut && (
              <button type="button" className="btn-primary text-xs w-full justify-center sm:w-auto sm:ml-auto shrink-0" onClick={() => ouvrir(seul)} data-testid="calendrier-prives-modifier">
                <Pencil size={13} /> {editables.length === 1 ? "Modifier mes disponibilités" : "Modifier les disponibilités"}
              </button>
            )}
          </div>
          {j?.ferme && (
            <div className="px-4 py-2 text-xs border-b" style={{ borderColor: TRAIT, background: "#F8FAFC", color: "#64748B" }}>
              École fermée (vacances ou férié) : pas de cours privé, sauf un jour ouvert à part{peut ? " (Modifier)" : ""}.
            </div>
          )}
          {!j ? <p className="p-4 text-sm text-[var(--muted)]">Chargement…</p> : (
            <div className="flex overflow-x-auto">
              <div className="w-11 shrink-0 relative" style={{ height: haut + 48 }}>
                {heures.map((m) => (
                  <span key={m} className="absolute right-1.5 text-[10px] text-[var(--muted)] tabular-nums -translate-y-1/2" style={{ top: 36 + ((m - debut) / 60) * H }}>{m / 60}h</span>
                ))}
              </div>
              {profs.map((p) => {
                const x = j.profs[p.prof_id] || vide;
                const modifiable = peut && editables.includes(p.prof_id);
                return (
                  <div key={p.prof_id} className="flex-1 min-w-[112px] border-l" style={{ borderColor: TRAIT }} data-testid={`calendrier-prives-${j.date}-${p.prof_id}`}>
                    <div className="h-9 px-2 flex items-center gap-1.5 border-b text-xs font-semibold" style={{ borderColor: TRAIT }}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.couleur }} />
                      <span className="truncate">{p.prenom}</span>
                      {modifiable && (
                        <button type="button" className="ml-auto p-1 rounded-full text-[var(--muted)] hover:bg-[#F1F5F9] hover:text-[var(--text)]" onClick={() => ouvrir(p.prof_id)}
                          aria-label={`Modifier les disponibilités de ${p.prenom}`} data-testid={`calendrier-prives-modifier-${p.prof_id}`}><Pencil size={12} /></button>
                      )}
                    </div>
                    <div className={`relative ${onAjouter || modifiable ? "cursor-pointer" : ""}`} style={{ height: haut, marginBottom: 12 }}
                      data-testid={`calendrier-prives-colonne-${p.prof_id}`}
                      onMouseMove={onAjouter ? (e) => { const m = surUnCours(e) ? null : quartDHeure(e); setSurvol(m === null ? null : { prof_id: p.prof_id, m }); } : undefined}
                      onMouseLeave={onAjouter ? () => setSurvol(null) : undefined}
                      onClick={(e) => {
                        if (onAjouter) {
                          if (!surUnCours(e)) onAjouter({ prof_id: p.prof_id, date: jourChoisi, debut: enHeure(quartDHeure(e)) });
                          return;
                        }
                        if (modifiable && e.target === e.currentTarget) ouvrir(p.prof_id);
                      }}>
                      {heures.map((m) => <div key={m} className="absolute left-0 right-0 border-t pointer-events-none" style={{ top: ((m - debut) / 60) * H, borderColor: "#F1F5F9" }} />)}
                      {survol && survol.prof_id === p.prof_id && (
                        <div className="absolute left-1 right-1 rounded-md px-1.5 text-[10px] font-semibold pointer-events-none z-10 flex items-center gap-1"
                          style={{ top: ((survol.m - debut) / 60) * H, height: H / 4, background: "#0F172A", color: "#fff", opacity: 0.85 }}>
                          <Plus size={10} /> {heure(enHeure(survol.m))}
                        </div>
                      )}
                      {(x.cours || []).map((c) => bloc(c.debut, c.fin, STYLE_COURS, c.nom, `${heure(c.debut)}–${heure(c.fin)}`,
                        "calendrier-prives-cours", `${c.nom} · ${heure(c.debut)}–${heure(c.fin)} (cours de l'école)`))}
                      {sansSesCours(x.libres || x.dispos, x.cours).map((c) => bloc(c.debut, c.fin, styleLibre(p.couleur), "Disponible", `${heure(c.debut)}–${heure(c.fin)}`,
                        "calendrier-prives-libre", `${p.prenom} · disponible ${heure(c.debut)}–${heure(c.fin)}${onAjouter ? " — toucher une heure pour ajouter un cours" : modifiable ? " — toucher pour modifier" : ""}`,
                        modifiable && !onAjouter ? () => ouvrir(p.prof_id) : null))}
                      {(x.reservations || []).map((r) => {
                        const eleve = r.eleve_prenom ? `${r.eleve_prenom} ${r.eleve_nom || ""}`.trim() : "";
                        const court = enMin(r.fin) - enMin(r.debut) < 45;
                        // Un cours réservé : l'élève d'abord (qui vient), puis la discipline.
                        return bloc(r.debut, r.fin, STYLE_RESERVE, court ? [r.discipline_label, eleve].filter(Boolean).join(" · ") : (eleve || r.discipline_label || "Cours privé"),
                          court ? heure(r.debut) : `${heure(r.debut)}–${heure(r.fin)}${eleve && r.discipline_label ? ` · ${r.discipline_label}` : ""}`,
                          "calendrier-prives-reserve", `Cours privé réservé · ${r.discipline_label || ""} ${heure(r.debut)}–${heure(r.fin)}${eleve ? ` · ${eleve}` : ""}`);
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* La légende de la journée : les mêmes styles que les blocs. */}
          {j && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 border-t text-[11px] text-[var(--muted)]" style={{ borderColor: TRAIT }}
              data-testid="calendrier-prives-legende-jour">
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={STYLE_RESERVE} /> réservé</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={styleLibre(profs.length === 1 ? profs[0].couleur : "#64748B")} /> disponible{profs.length > 1 ? " (teinte du prof)" : ""}
              </span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={STYLE_COURS} /> cours de l'école</span>
              {/* La pause des Réglages › Cours privés (02.11.2026). */}
              <span>Une seule salle : un cours réservé prend l'heure à tous les profs, {cal.pause ?? 15} min de pause avant et après.</span>
              {onAjouter && <span className="font-medium" style={{ color: "var(--text)" }}>Toucher une heure : ajouter un cours.</span>}
            </div>
          )}
        </div>
      </div>

      {/* La semaine type de chaque prof : un jour par pastille ; toucher = « tous les … ». */}
      <div className="rounded-2xl border bg-white" style={{ borderColor: TRAIT }} data-testid="calendrier-prives-semaines">
        <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Semaine type</div>
        {cal.profs.filter((p) => !seul || p.prof_id === seul).map((p, i) => {
          const modifiable = editables.includes(p.prof_id) && onEnregistrer;
          return (
            <div key={p.prof_id} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" style={i ? { borderTop: `1px solid ${TRAIT}` } : undefined}
              data-testid={`calendrier-prives-semaine-${p.prof_id}`}>
              <span className="w-24 flex items-center gap-1.5 text-sm font-semibold"><span className="w-2 h-2 rounded-full" style={{ background: p.couleur }} />{p.prenom}</span>
              <div className="flex flex-wrap gap-1.5 flex-1">
                {JOURS_SEMAINE.map((nom, js) => {
                  const ps = (p.semaine || []).filter((s) => s.jour === js);
                  const contenu = <><span className="font-semibold">{nom}</span> <span className={ps.length ? "" : "text-[var(--muted)]"}>{ps.length ? plagesTexte(ps) : "—"}</span></>;
                  return modifiable ? (
                    <button key={nom} type="button" onClick={() => ouvrir(p.prof_id, prochain(js), true)} data-testid={`calendrier-prives-semaine-${p.prof_id}-${js}`}
                      className="rounded-full border px-2.5 py-1 text-xs hover:bg-[#F8FAFC] tabular-nums" style={{ borderColor: TRAIT }}>{contenu}</button>
                  ) : <span key={nom} className="rounded-full px-2.5 py-1 text-xs tabular-nums" style={{ background: "#F8FAFC" }}>{contenu}</span>;
                })}
              </div>
            </div>
          );
        })}
      </div>

      {edition && (
        <Editeur key={`${edition.prof_id}-${edition.date}-${edition.toutes}`} cal={cal} edition={edition} editables={editables}
          onFermer={() => setEdition(null)} onEnregistrer={onEnregistrer} />
      )}
    </div>
  );
}
